import { ArrowRight, MessageCircle, Phone, Sparkles } from "lucide-react";
import { BRAND } from "../../../data/content";

export default function HeroSection({ variant, content, accent, image, onBook }) {
  const waLink = `https://wa.me/${BRAND.phoneRaw.replace("+", "")}?text=${encodeURIComponent(
    `Hi GenieX, I'd like to enquire about ${content.title}.`
  )}`;

  const Eyebrow = () => (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${accent.chip} text-[11px] tracking-[0.18em] uppercase`}>
      <Sparkles className="w-3 h-3" /> {content.eyebrow}
    </div>
  );

  const CTAs = () => (
    <div className="flex flex-wrap items-center gap-3 mt-10">
      <button onClick={onBook} data-testid="hero-cta-book" className="btn-gold">
        Book a Consultation <ArrowRight className="w-4 h-4" />
      </button>
      <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp" className="btn-outline">
        <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
      </a>
      <a href={`tel:${BRAND.phoneRaw}`} data-testid="hero-cta-call" className="text-[12px] tracking-wide text-dim hover:text-gold transition-colors inline-flex items-center gap-2 ml-2">
        <Phone className="w-3.5 h-3.5" /> {BRAND.phone}
      </a>
    </div>
  );

  if (variant === "fullbleed") {
    return (
      <section data-testid="hero-section-fullbleed" className="relative min-h-[92svh] flex items-end pb-20 pt-28 overflow-hidden">
        <img src={image} alt={content.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--gx-bg)] via-[var(--gx-bg)]/75 to-black/10" />
        <div className="absolute inset-0" style={{ background: accent.radial }} />
        <div className="relative max-w-[1400px] w-full mx-auto px-5 lg:px-10">
          <Eyebrow />
          <h1 className="font-display tracking-tight text-white mt-7 text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] max-w-[22ch]">
            {content.tagline}
          </h1>
          <p className="lead mt-6 max-w-[58ch]">{content.intro}</p>
          <CTAs />
        </div>
      </section>
    );
  }

  if (variant === "framed") {
    return (
      <section data-testid="hero-section-framed" className="relative min-h-[88svh] flex items-center pt-28 lg:pt-36 pb-16 lg:pb-24">
        <div className="absolute inset-0 pointer-events-none" style={{ background: accent.radial }} />
        <div className="relative max-w-[1400px] w-full mx-auto px-5 lg:px-10 grid lg:grid-cols-[1fr_0.85fr] gap-12 lg:gap-16 items-center">
          <div>
            <Eyebrow />
            <h1 className="font-display tracking-tight text-white mt-7 text-4xl sm:text-5xl lg:text-[60px] leading-[1.05] max-w-[19ch]">
              {content.tagline}
            </h1>
            <p className="lead mt-7 max-w-[56ch]">{content.intro}</p>
            <CTAs />
          </div>
          <div className="relative aspect-[4/5] w-full max-w-[440px] mx-auto">
            <div className="absolute -inset-6 rounded-[40px] border border-line/60" />
            <div className={`absolute -inset-3 rounded-[34px] bg-gradient-to-br ${accent.ring} blur-2xl opacity-60`} />
            <div className="absolute inset-0 rounded-[28px] overflow-hidden border border-white/10">
              <img src={image} alt={content.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            </div>
            {["top-3 left-3", "top-3 right-3", "bottom-3 left-3", "bottom-3 right-3"].map((p) => (
              <div key={p} className={`absolute ${p} w-2 h-2 rounded-full bg-gold/70`} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  const imageLeft = variant === "split-left";
  return (
    <section data-testid={`hero-section-${variant}`} className="relative min-h-[88svh] flex items-center pt-28 lg:pt-36 pb-16 lg:pb-24">
      <div className="absolute inset-0 pointer-events-none" style={{ background: accent.radial }} />
      <div className="relative max-w-[1400px] w-full mx-auto px-5 lg:px-10 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-center">
        <div className={imageLeft ? "lg:order-2" : ""}>
          <Eyebrow />
          <h1 className="font-display tracking-tight text-white mt-7 text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] max-w-[18ch]">
            {content.tagline}
          </h1>
          <p className="lead mt-7 max-w-[58ch]">{content.intro}</p>
          <CTAs />
        </div>
        <div className={`relative aspect-square w-full max-w-[500px] mx-auto rounded-[32px] overflow-hidden border border-line/70 ${imageLeft ? "lg:order-1" : ""}`}>
          <img src={image} alt={content.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
        </div>
      </div>
    </section>
  );
}
